// QuestionMediaSection.jsx - Media attachment for question creation
// Place in: src/components/leitner/QuestionMediaSection.jsx

import React, { useState } from 'react';
import { 
  Image, Video, Code, X, Upload, Plus, ChevronLeft, ChevronRight, 
  Youtube, Link, Trash2, Play, Pause
} from 'lucide-react';

const MEDIA_TYPES = [
  { id: 'none', label: 'None', icon: X },
  { id: 'images', label: 'Images', icon: Image },
  { id: 'video', label: 'Video', icon: Video },
  { id: 'embed', label: 'URL/HTML', icon: Code }
];

const QuestionMediaSection = ({ value, onChange, label = "Media Attachment (Optional)" }) => {
  const [previewIndex, setPreviewIndex] = useState(0);
  const [videoType, setVideoType] = useState('youtube');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);

  const media = value || { type: 'none', images: [], video: null, embed: null };

  const handleTypeChange = (type) => {
    onChange({ ...media, type });
    setPreviewIndex(0);
  };

  // Image handlers
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024);
    
    if (validFiles.length < files.length) {
      alert('Some images were skipped (max 10MB each)');
    }

    const newImages = [...(media.images || []), ...validFiles].slice(0, 10);
    onChange({ ...media, type: 'images', images: newImages });
  };

  const removeImage = (index) => {
    const newImages = media.images.filter((_, i) => i !== index);
    onChange({ ...media, images: newImages });
    if (previewIndex >= newImages.length && previewIndex > 0) {
      setPreviewIndex(previewIndex - 1);
    }
  };

  const moveImage = (fromIndex, direction) => {
    const newImages = [...media.images];
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= newImages.length) return;
    [newImages[fromIndex], newImages[toIndex]] = [newImages[toIndex], newImages[fromIndex]];
    onChange({ ...media, images: newImages });
  };

  // Video handlers
  const getYoutubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const handleYoutubeSubmit = () => {
    const videoId = getYoutubeId(youtubeUrl);
    if (videoId) {
      onChange({
        ...media,
        type: 'video',
        video: {
          source: 'youtube',
          url: youtubeUrl,
          videoId,
          thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
        }
      });
      setYoutubeUrl('');
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        alert('Video must be less than 100MB');
        return;
      }
      onChange({
        ...media,
        type: 'video',
        video: {
          source: 'upload',
          file,
          url: URL.createObjectURL(file),
          name: file.name
        }
      });
    }
  };

  const removeVideo = () => {
    onChange({ ...media, video: null });
    setYoutubeUrl('');
  };

  // Embed handlers
  const handleEmbedChange = (field, val) => {
    onChange({
      ...media,
      type: 'embed',
      embed: { ...(media.embed || {}), [field]: val }
    });
  };

  const getImageSrc = (img) => {
    if (img instanceof File) return URL.createObjectURL(img);
    return img.url || img;
  };

  // Auto slideshow
  React.useEffect(() => {
    if (!isPlaying || media.type !== 'images' || !media.images?.length) return;
    const interval = setInterval(() => {
      setPreviewIndex(p => (p + 1) % media.images.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isPlaying, media.type, media.images?.length]);

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
        <Image size={16} className="text-indigo-400" />
        {label}
      </label>

      {/* Type Selector */}
      <div className="flex gap-1 mb-3 bg-slate-700/50 p-1 rounded-lg">
        {MEDIA_TYPES.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => handleTypeChange(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-sm font-medium transition-all ${
              media.type === t.id
                ? 'bg-indigo-600 text-white shadow'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <t.icon size={14} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Images Section */}
      {media.type === 'images' && (
        <div className="space-y-3">
          {/* Image Preview - Styled like Video Preview */}
          {media.images?.length > 0 && (
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  <Image size={14} className="text-indigo-400" />
                  {media.images.length} Image{media.images.length > 1 ? 's' : ''} Added
                </span>
                <button
                  type="button"
                  onClick={() => onChange({ ...media, images: [] })}
                  className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                  title="Remove all images"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Main Preview */}
              <div className="relative aspect-video bg-slate-800 rounded-lg overflow-hidden mb-2">
                <img
                  src={getImageSrc(media.images[previewIndex])}
                  alt={`Preview ${previewIndex + 1}`}
                  className="w-full h-full object-contain"
                />
                
                {/* Navigation Arrows */}
                {media.images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setPreviewIndex(p => (p - 1 + media.images.length) % media.images.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewIndex(p => (p + 1) % media.images.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                    >
                      <ChevronRight size={18} />
                    </button>

                    {/* Autoplay & Counter */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setIsPlaying(p => !p)}
                        className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                        title={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                      >
                        {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                      </button>
                      <span className="px-2 py-1 bg-black/60 rounded-full text-white text-xs">
                        {previewIndex + 1} / {media.images.length}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {media.images.map((img, idx) => (
                  <div key={idx} className="relative flex-shrink-0 group">
                    <button
                      type="button"
                      onClick={() => setPreviewIndex(idx)}
                      className={`w-14 h-14 rounded-md overflow-hidden border-2 transition ${
                        previewIndex === idx ? 'border-indigo-500' : 'border-transparent hover:border-slate-500'
                      }`}
                    >
                      <img src={getImageSrc(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute -top-1 -right-1 p-0.5 bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition"
                    >
                      <X size={10} />
                    </button>
                    {/* Reorder buttons */}
                    <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => moveImage(idx, 'left')}
                          className="p-0.5 bg-slate-700 rounded text-white"
                        >
                          <ChevronLeft size={10} />
                        </button>
                      )}
                      {idx < media.images.length - 1 && (
                        <button
                          type="button"
                          onClick={() => moveImage(idx, 'right')}
                          className="p-0.5 bg-slate-700 rounded text-white"
                        >
                          <ChevronRight size={10} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {(media.images?.length || 0) < 10 && (
            <label className="flex items-center justify-center w-full p-4 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-slate-700/50 transition group">
              <Plus size={20} className="mr-2 text-slate-400 group-hover:text-indigo-400" />
              <span className="text-slate-400 group-hover:text-slate-300">
                {media.images?.length ? 'Add More Images' : 'Upload Images'} (max 10)
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          )}
        </div>
      )}

      {/* Video Section */}
      {media.type === 'video' && (
        <div className="space-y-3">
          {media.video ? (
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  {media.video.source === 'youtube' ? (
                    <Youtube size={14} className="text-red-400" />
                  ) : (
                    <Video size={14} className="text-blue-400" />
                  )}
                  {media.video.source === 'youtube' ? 'YouTube Video' : media.video.name}
                </span>
                <button
                  type="button"
                  onClick={removeVideo}
                  className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              
              {media.video.source === 'youtube' && media.video.thumbnail && (
                <div className="relative rounded-md overflow-hidden aspect-video">
                  <img 
                    src={media.video.thumbnail} 
                    alt="Video thumbnail" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center">
                      <Play size={24} className="text-white ml-1" />
                    </div>
                  </div>
                </div>
              )}
              
              {media.video.source === 'upload' && (
                <video controls className="w-full rounded-md max-h-48" src={media.video.url} />
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2 bg-slate-700/30 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setVideoType('youtube')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition ${
                    videoType === 'youtube' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Youtube size={14} />
                  YouTube
                </button>
                <button
                  type="button"
                  onClick={() => setVideoType('upload')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition ${
                    videoType === 'upload' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Upload size={14} />
                  Upload
                </button>
              </div>

              {videoType === 'youtube' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="Paste YouTube URL..."
                    className="flex-1 bg-slate-700 text-sm border border-slate-600 rounded-md p-2.5 text-slate-100 focus:ring-2 focus:ring-red-500/50"
                  />
                  <button
                    type="button"
                    onClick={handleYoutubeSubmit}
                    disabled={!getYoutubeId(youtubeUrl)}
                    className="px-4 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-md transition"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full p-4 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-slate-700/50 transition group">
                  <Video size={20} className="mr-2 text-slate-400 group-hover:text-blue-400" />
                  <span className="text-slate-400 group-hover:text-slate-300">Upload Video (max 100MB)</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}
        </div>
      )}

      {/* Embed Section */}
      {media.type === 'embed' && (
        <div className="space-y-3">
          <div className="flex gap-2 bg-slate-700/30 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => handleEmbedChange('embedType', 'url')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition ${
                media.embed?.embedType !== 'html' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Link size={14} />
              URL
            </button>
            <button
              type="button"
              onClick={() => handleEmbedChange('embedType', 'html')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition ${
                media.embed?.embedType === 'html' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Code size={14} />
              HTML
            </button>
          </div>

          {media.embed?.embedType === 'html' ? (
            <div>
              <textarea
                value={media.embed?.html || ''}
                onChange={(e) => handleEmbedChange('html', e.target.value)}
                placeholder="Paste embed HTML code (iframes, widgets, etc.)..."
                rows={4}
                className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-3 text-slate-100 font-mono focus:ring-2 focus:ring-purple-500/50"
              />
              <p className="text-xs text-slate-500 mt-1">
                Supports iframes, embed codes from external services
              </p>
            </div>
          ) : (
            <div>
              <input
                type="url"
                value={media.embed?.url || ''}
                onChange={(e) => handleEmbedChange('url', e.target.value)}
                placeholder="Enter URL to embed (e.g., https://example.com/widget)"
                className="w-full bg-slate-700 text-sm border border-slate-600 rounded-md p-2.5 text-slate-100 focus:ring-2 focus:ring-indigo-500/50"
              />
              <p className="text-xs text-slate-500 mt-1">
                URL will be loaded in an iframe
              </p>
            </div>
          )}

          {/* Preview */}
          {(media.embed?.url || media.embed?.html) && (
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  <Code size={14} className="text-purple-400" />
                  {media.embed?.embedType === 'html' ? 'HTML Embed' : 'URL Embed'}
                </span>
                <button
                  type="button"
                  onClick={() => onChange({ ...media, embed: null })}
                  className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-800">
                {media.embed?.embedType === 'html' ? (
                  <iframe
                    srcDoc={`<!DOCTYPE html><html><head><style>body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#1e293b;}</style></head><body>${media.embed.html}</body></html>`}
                    className="w-full h-full"
                    sandbox="allow-scripts allow-same-origin"
                    title="Embed preview"
                  />
                ) : (
                  <iframe
                    src={media.embed.url}
                    className="w-full h-full"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    title="URL preview"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-500 mt-2">
        Add images (slideshow), video, or embed external content. Only one type per question.
      </p>
    </div>
  );
};

export default QuestionMediaSection;